import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Lock, Eye, EyeOff, ShieldCheck, X } from "lucide-react"
import FinancialTab from "./FinancialTab"

// Hash simple para no exponer la contraseña en texto plano en el bundle
// Contraseña: ScienceFit#26
const PWD_HASH = "5369656e636546697423323636"

function strToHex(s: string) {
  return Array.from(s).map((c) => c.charCodeAt(0).toString(16).padStart(2, "0")).join("")
}

const SESSION_KEY = "sciencefitness_financial_unlocked"

export default function FinancialGate() {
  const [unlocked, setUnlocked] = useState(false)
  const [input, setInput]       = useState("")
  const [showPwd, setShowPwd]   = useState(false)
  const [error, setError]       = useState(false)
  const [shake, setShake]       = useState(false)
  const inputRef                = useRef<HTMLInputElement>(null)

  // Restaurar sesión si ya se autenticó antes
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === "1") setUnlocked(true)
  }, [])

  useEffect(() => {
    if (!unlocked) setTimeout(() => inputRef.current?.focus(), 200)
  }, [unlocked])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (strToHex(input) === PWD_HASH) {
      sessionStorage.setItem(SESSION_KEY, "1")
      setError(false)
      setUnlocked(true)
    } else {
      setError(true)
      setShake(true)
      setInput("")
      setTimeout(() => setShake(false), 500)
    }
  }

  function handleLock() {
    sessionStorage.removeItem(SESSION_KEY)
    setUnlocked(false)
    setInput("")
    setError(false)
  }

  if (unlocked) {
    return (
      <div className="relative">
        {/* Botón cerrar sesión financiera */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={handleLock}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted hover:text-white hover:border-white/30 transition"
          >
            <Lock size={11} />
            Bloquear sección
          </button>
        </div>
        <FinancialTab />
      </div>
    )
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <AnimatePresence>
        <motion.div
          key="gate"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={shake ? { x: [-8, 8, -6, 6, -4, 4, 0] } : { opacity: 1, scale: 1 }}
          transition={shake ? { duration: 0.4 } : { duration: 0.2 }}
          className="w-full max-w-sm space-y-6"
        >
          {/* Icono */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 border border-primary/20">
              <Lock size={28} className="text-primary" />
            </div>
            <div className="text-center">
              <h2 className="text-lg font-bold text-white tracking-wide">Modelo Financiero</h2>
              <p className="text-sm text-muted mt-1">Ingresá la contraseña para acceder</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <input
                ref={inputRef}
                type={showPwd ? "text" : "password"}
                value={input}
                onChange={(e) => { setInput(e.target.value); setError(false) }}
                placeholder="Contraseña"
                className={`w-full rounded-xl border px-4 py-3 pr-11 text-sm text-white bg-surface-elevated focus:outline-none transition ${
                  error
                    ? "border-red-500/60 focus:border-red-500"
                    : "border-white/10 focus:border-primary"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-white transition"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <AnimatePresence>
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 text-xs text-red-400"
                >
                  <X size={12} />
                  Contraseña incorrecta. Intentá de nuevo.
                </motion.p>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={!input.trim()}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan disabled:opacity-40"
            >
              <ShieldCheck size={16} />
              Acceder
            </button>
          </form>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
